// lib/feed/feed-snapshot.ts
//
// Pure domain for the paginated-feed sessionStorage snapshot cache (hex: this is
// the functional core — key derivation, (de)serialization + TTL freshness,
// item-capping, LRU eviction). It has NO DOM / window / sessionStorage access:
// the hook (`hooks/use-paginated-feed.ts`, the adapter) does all storage +
// scroll I/O and calls into these functions. Keeping the decisions pure makes
// them unit-testable without React/jsdom and mirrors the project idiom in
// `hooks/use-my-seller-orders.ts` (sessionStorage + TTL + keyed invalidation).
//
// @see hooks/use-paginated-feed.ts
// @see .tondocs/skills/hex.md §1 (functional core, imperative shell)

import type { ListingResult } from "@kraahl/contract";

/** Namespace all feed snapshot keys so eviction can enumerate them cheaply. */
export const FEED_SNAPSHOT_PREFIX = "feed:snapshot:";

/** Bump when the persisted shape changes — old entries are then rejected on read. */
export const FEED_SNAPSHOT_VERSION = 1;

/**
 * ~3 min TTL. Long enough that the common listing→back round-trip (a few
 * seconds to a minute) paints instantly from cache; short enough that a
 * returning visitor isn't shown sold-out / mutated stock for long. Within the
 * window the hook still fires a background SWR revalidate, so even a fresh-ish
 * hit reconciles price/availability — the TTL only bounds how stale the FIRST
 * paint may be, not how long stale data survives.
 */
export const FEED_SNAPSHOT_TTL_MS = 3 * 60_000;

/**
 * Cap persisted items per entry so one snapshot can't blow the ~5 MB
 * sessionStorage budget. If the live list exceeds this we persist the head and
 * force `hasNextPage: true` so the tail is re-fetched via loadMore after hydrate.
 */
export const FEED_SNAPSHOT_MAX_ITEMS = 120;

/** Cap distinct filter-combo entries; oldest (by write ts) evicted first. */
export const FEED_SNAPSHOT_MAX_ENTRIES = 8;

export interface FeedSnapshot {
  v: number;
  items: ListingResult[];
  page: number;
  hasNextPage: boolean;
  nextCursor: string | null;
  scrollTop: number;
  ts: number;
}

/**
 * Everything that parameterizes the feed query. Two calls with the same
 * `FeedSnapshotQuery` return the same rows, so this IS the cache identity —
 * different filter/sort/search combos derive different keys (task req 4:
 * invalidation-by-key is free because the key includes the params).
 */
export interface FeedSnapshotQuery {
  mode: string;
  userId: string | undefined;
  limit: number;
  /** Already-normalized JSON blob of filters + search + slugs + sortBy. */
  filtersKey: string;
}

/**
 * Derive the sessionStorage key for a (consumer, query) pair. `cacheKey`
 * namespaces the consumer instance (e.g. "live"); the query object is JSON so
 * that a change in ANY parameter yields a different key. `filtersKey` may itself
 * contain `:`/`{}` — it lives inside the JSON value, never spliced raw into the
 * key, so the prefix stays a clean, enumerable namespace.
 */
export function buildFeedSnapshotKey(cacheKey: string, query: FeedSnapshotQuery): string {
  const identity = JSON.stringify({
    m: query.mode,
    u: query.userId ?? "anon",
    l: query.limit,
    f: query.filtersKey,
  });
  return `${FEED_SNAPSHOT_PREFIX}${cacheKey}:${identity}`;
}

/**
 * Parse + validate a raw sessionStorage value into a fresh snapshot, or null.
 * Rejects: missing/mismatched version, non-array items, expired ts, empty items
 * (nothing worth hydrating). Pure — `now`/`ttlMs` are injected so tests are
 * deterministic and the domain never reads the clock.
 */
export function parseFeedSnapshot(
  raw: string | null,
  now: number,
  ttlMs: number,
): FeedSnapshot | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const p = parsed as Partial<FeedSnapshot>;
  if (p.v !== FEED_SNAPSHOT_VERSION) return null;
  if (!Array.isArray(p.items) || p.items.length === 0) return null;
  const ts = typeof p.ts === "number" ? p.ts : 0;
  if (now - ts > ttlMs) return null;
  return {
    v: FEED_SNAPSHOT_VERSION,
    items: p.items as ListingResult[],
    page: Number.isFinite(p.page) ? Number(p.page) : 0,
    hasNextPage: typeof p.hasNextPage === "boolean" ? p.hasNextPage : true,
    nextCursor: typeof p.nextCursor === "string" ? p.nextCursor : null,
    scrollTop: Number.isFinite(p.scrollTop) ? Math.max(0, Number(p.scrollTop)) : 0,
    ts,
  };
}

/**
 * Serialize a snapshot for storage, capping items. If the live list was longer
 * than `maxItems` we keep the head AND set `hasNextPage: true` — the tail beyond
 * the cap is recoverable via loadMore, so we must not signal "end of feed".
 */
export function serializeFeedSnapshot(
  input: Omit<FeedSnapshot, "v">,
  maxItems: number = FEED_SNAPSHOT_MAX_ITEMS,
): string {
  const truncated = input.items.length > maxItems;
  const items = truncated ? input.items.slice(0, maxItems) : input.items;
  const snapshot: FeedSnapshot = {
    v: FEED_SNAPSHOT_VERSION,
    items,
    page: input.page,
    hasNextPage: truncated ? true : input.hasNextPage,
    nextCursor: input.nextCursor,
    scrollTop: Math.max(0, input.scrollTop),
    ts: input.ts,
  };
  return JSON.stringify(snapshot);
}

/**
 * Given every snapshot entry currently in storage (key + write ts) and a cap,
 * return the keys to evict so at most `maxEntries` (newest) remain. Pure — the
 * adapter enumerates storage, calls this, then removes the returned keys.
 */
export function selectSnapshotEvictions(
  entries: { key: string; ts: number }[],
  maxEntries: number = FEED_SNAPSHOT_MAX_ENTRIES,
): string[] {
  if (entries.length <= maxEntries) return [];
  const newestFirst = [...entries].sort((a, b) => b.ts - a.ts);
  return newestFirst.slice(maxEntries).map((entry) => entry.key);
}
